import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink, Lock, MapPin, Plus, Star, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { milestoneMeta, SectionCard } from "./ui";
import { DesignOrderForm } from "./DesignOrderForm";
import { useTaskPolling } from "../hooks";
import {
  createWodyCrossing, createWodyOrder, deleteWodyCrossing, getWodyContractors,
  getWodyPanel, recomputeWody, setWodyMode, simulateWodyPrices, suggestWodyItems,
  updateWodyCrossing,
} from "../api";
import { setGraphNodeState } from "../advisor/api";
import type { HydroCrossingKind, HydroCrossingRow, WodyMode, WodySimulatedContractor } from "../api";
import type { MilestoneState } from "../types";
import { apiErr } from "../utils";
import { fmtDate } from "../utils";

export const wodyQueryKey = (connectionId: string) => ["pp", "wody", connectionId] as const;

const KIND_COLORS: Record<string, string> = {
  zblizenie: "#d97706",
  przejscie_ziemne: "#2563eb",
  przejscie_napowietrzne: "#7c3aed",
  przebudowa_napowietrzna: "#0d9488",
  demontaz_napowietrzny: "#64748b",
  teren_zalewowy: "#0284c7",
};

// Tereny zalewowe z map zagrożenia powodziowego Wód Polskich (ISOK) — ta sama usługa, z której detekcja liczy strefy (apps/geo/services/mzp_floods.py).
// WMS 1.3.0 obsługuje EPSG:3857, więc kafle Leafleta idą wprost; warstwy 0 = od strony
// rzek, 1 = od strony morza.
const MZP_WMS =
  "https://wody.isok.gov.pl/gpservices/KZGW/MZP20_ObszarySzczegolnegoZagrozeniaPowodzia/MapServer/WMSServer";

const EVENT_BADGE: Record<string, string> = {
  przeciecie_cieku: "Przecięcie cieku",
  wejscie_dzialka_wp: "Działka WP",
  zblizenie_wal: "Wał <50 m",
  teren_zalewowy: "Teren zalewowy",
  reczne: "Ręczne",
};

// Kryteria detekcji — wprost przy każdym zdarzeniu (dymek), żeby nie zgadywać,
// skąd wzięła się pozycja wykazu.
const EVENT_CRITERIA: Record<string, string> = {
  przeciecie_cieku:
    "Kryterium: oś trasy przecina ciek z Mapy Podziału Hydrograficznego Polski (Hydroportal Wód Polskich). Przekroczenie wymaga warunków przejścia i pozwolenia/zgłoszenia wodnoprawnego.",
  wejscie_dzialka_wp:
    "Kryterium: trasa wchodzi na działkę, której właścicielem/władającym są Wody Polskie (lub użytek Wp/Ws), bez przecięcia osi cieku. Zajęcie gruntu pokrytego wodami rozlicza się jak PRZEJŚCIE (rodzaj z otypowanych linii trasy) — „zbliżenie” dotyczy wyłącznie cieku/wału.",
  zblizenie_wal:
    "Kryterium: trasa przebiega bliżej niż 50 m od obiektu wałowego z BDOT10k (art. 176 ust. 1 pkt 4 Prawa wodnego zakazuje robót <50 m od stopy wału; zwolnienie = decyzja WP). Miarka na mapie pokazuje zmierzoną odległość. Uwaga: warstwa BDOT10k zawiera też groble i nasypy — obiekt „rodzaj nieustalony” zweryfikuj i w razie czego wyklucz.",
  teren_zalewowy:
    "Kryterium: odcinek trasy albo urządzenie projektu (stacja, złącze, słup) leży na terenie zalewowym (mapy zagrożenia powodziowego Wód Polskich). Projektowanie urządzeń na terenie zalewowym każdorazowo wymaga operatu wodnoprawnego — także bez cieku i wału w pobliżu. Odcinki i urządzenia bliżej niż 150 m od siebie to jedna pozycja; tam, gdzie teren zalewowy rozlicza już przejście albo zbliżenie na tym terenie, osobnej pozycji nie ma.",
  reczne: "Pozycja dodana ręcznie przez zespół.",
};

/** Pozycja „teren zalewowy" leży w strefie z definicji — także dodana ręcznie, kiedy
 *  flaga z detekcji (in_oszp_zone) jest pusta. */
const inFloodZone = (c: HydroCrossingRow) => c.in_oszp_zone || c.kind === "teren_zalewowy";

/** Zakładka „Wody Polskie" projektu ZE: przełącznik trybu (robimy my / robi
 *  podwykonawca), detekcja przekroczeń i zbliżeń (cieki z Hydroportalu, wały z
 *  BDOT10k, strefy OSZP z MZP), edytowalny wykaz zdarzeń i — w trybie podwykonawcy —
 *  symulacja cen kandydatów + zlecenie tą samą drogą co MDCP. */
export function WodyTab({ connection }: { connection: { id: string } }) {
  const connectionId = connection.id;
  const { user } = useAuthStore();
  // Widok zamówienia (/orders/:id) jest za TechnicalDirectorGuard — dyrektor
  // nietechniczny dostałby ciche przekierowanie na „/" (wzór ContractorsTab).
  const canOpenOrder =
    !!user && !(user.role === "director" && !user.is_technical_director && !user.is_ceo);
  const qc = useQueryClient();
  const key = wodyQueryKey(connectionId);
  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => getWodyPanel(connectionId),
    staleTime: 15_000,
  });
  // Zakładka zmienia dane, które widać POZA nią: licznik „do zlecenia" i stany kamieni
  // jadą payloadem projektu (["pp","by-project"]), a odznaka zakładki własnym zapytaniem.
  // Bez tych trzech unieważnień karta kamienia świeciła starą liczbą aż do F5.
  const inval = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ["pp", "by-project"] });
    qc.invalidateQueries({ queryKey: ["pp", "connection", connectionId] });
    qc.invalidateQueries({ queryKey: ["pp", "attention", connectionId] });
    qc.invalidateQueries({ queryKey: ["pp", "graph-proposals", connectionId] });
  };

  const [taskId, setTaskId] = useState<string | null>(null);
  const liveTaskId = taskId ?? (data?.analysis.task && !["success", "failure", "cancelled"].includes(data.analysis.task.status)
    ? data.analysis.task.id
    : null);
  const task = useTaskPolling(liveTaskId);
  const taskDone = task.data && ["success", "failure", "cancelled"].includes(task.data.status);
  // Ostatni znany stan zadania: świeży polling wygrywa, ale po przeładowaniu strony
  // pokazujemy też błąd zapisany na analizie — nieudana detekcja nie może wyglądać
  // jak „nigdy nie liczono" bez słowa wyjaśnienia.
  const lastTask = task.data ?? data?.analysis.task ?? null;
  // Po zakończeniu zadania odśwież panel (raz — status przestaje się zmieniać).
  useEffect(() => {
    if (taskDone) {
      setTaskId(null);
      simulateM.reset();
      inval();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskDone]);

  const modeM = useMutation({
    mutationFn: (mode: WodyMode) => setWodyMode(connectionId, mode),
    onSuccess: inval,
  });
  const recomputeM = useMutation({
    mutationFn: () => recomputeWody(connectionId),
    onSuccess: (d) => setTaskId(d.task_id),
  });
  const simulateM = useMutation({ mutationFn: () => simulateWodyPrices(connectionId) });
  // Zastosowanie niebieskiej propozycji stanu kamienia (np. po zleceniu → „Wysłane")
  // — ten sam endpoint co graf postępów, z powodem z faktu.
  const applyProposalM = useMutation({
    mutationFn: (v: { node: string; state: string; reason: string }) =>
      setGraphNodeState(connectionId, v.node, v.state, v.reason),
    onSuccess: inval,
  });
  // Zmiana wykazu unieważnia symulację cen — stare kwoty nie mogą wisieć przy
  // zmienionym zakresie (i blokować odświeżenia listy kandydatów).
  const invalAndResetSim = () => { simulateM.reset(); inval(); };
  const patchM = useMutation({
    mutationFn: (v: { id: string; patch: Parameters<typeof updateWodyCrossing>[1] }) =>
      updateWodyCrossing(v.id, v.patch),
    onSuccess: invalAndResetSim,
  });
  const delM = useMutation({ mutationFn: (id: string) => deleteWodyCrossing(id), onSuccess: invalAndResetSim });
  const addM = useMutation({
    mutationFn: (v: Parameters<typeof createWodyCrossing>[0]) => createWodyCrossing(v),
    onSuccess: () => { setAddOpen(false); invalAndResetSim(); },
  });
  const orderM = useMutation({
    mutationFn: (fd: FormData) => createWodyOrder(connectionId, fd),
    onSuccess: () => { setShowForm(false); setPickedContractor(null); inval(); },
  });

  const [showForm, setShowForm] = useState(false);
  const [pickedContractor, setPickedContractor] = useState<WodySimulatedContractor | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addDraft, setAddDraft] = useState({ lat: "", lng: "", kind: "zblizenie" as HydroCrossingKind, feature_name: "", note: "" });
  // Mapa wyników: trasa (czerwona), zdarzenia jako kolorowe punkty numerowane jak
  // w tabeli, odcinki trasy w strefie zalewowej (przerywany niebieski). Ta sama mapa
  // służy do „Wskaż na mapie" przy pozycji ręcznej (klik wypełnia lat/lng).
  // circleMarker zamiast domyślnych ikon Leafleta (brak assetów w bundlu — konwencja
  // MdcpContractorMap).
  const [pickOnMap, setPickOnMap] = useState(false);
  const picking = addOpen && pickOnMap;
  const pickingRef = useRef(picking);
  pickingRef.current = picking;
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const eventsLayerRef = useRef<L.LayerGroup | null>(null);
  const pickMarkerRef = useRef<L.CircleMarker | null>(null);
  const zonesLayerRef = useRef<L.TileLayer.WMS | null>(null);
  const [showZones, setShowZones] = useState(true);
  const hasMapContent = !!data && (
    picking
    || !!data.route_geojson
    || data.crossings.some((c) => c.lat != null && c.lng != null)
    || !!data.analysis.display_geom
  );

  const [mapReady, setMapReady] = useState(false);
  useEffect(() => {
    if (!hasMapContent || !mapDivRef.current || mapRef.current) return;
    const map = L.map(mapDivRef.current, { center: [52, 19], zoom: 6, attributionControl: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 21 }).addTo(map);
    map.on("click", (e: L.LeafletMouseEvent) => {
      if (!pickingRef.current) return;
      setAddDraft((d) => ({ ...d, lat: e.latlng.lat.toFixed(6), lng: e.latlng.lng.toFixed(6) }));
      if (pickMarkerRef.current) {
        pickMarkerRef.current.setLatLng(e.latlng);
      } else {
        pickMarkerRef.current = L.circleMarker(e.latlng, {
          radius: 9, color: "#dc2626", weight: 3, fillColor: "#dc2626", fillOpacity: 0.35,
        }).addTo(map);
      }
    });
    // Strefy zalewowe pod wynikami detekcji — widać, CO wpada w OSZP i dlaczego.
    zonesLayerRef.current = L.tileLayer.wms(MZP_WMS, {
      layers: "0,1", format: "image/png", transparent: true, version: "1.3.0",
      opacity: 0.45, maxZoom: 21,
    });
    eventsLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setMapReady(true);
    return () => {
      map.remove();
      mapRef.current = null;
      eventsLayerRef.current = null;
      zonesLayerRef.current = null;
      pickMarkerRef.current = null;
      setMapReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMapContent]);

  useEffect(() => {
    const map = mapRef.current;
    const zones = zonesLayerRef.current;
    if (!map || !zones) return;
    if (showZones) {
      if (!map.hasLayer(zones)) zones.addTo(map);
    } else if (map.hasLayer(zones)) {
      map.removeLayer(zones);
    }
  }, [showZones, mapReady]);

  // Kursor celownika w trybie wskazywania.
  useEffect(() => {
    if (mapDivRef.current) mapDivRef.current.style.cursor = picking ? "crosshair" : "";
  }, [picking]);

  // Warstwa danych przerysowywana przy każdej zmianie panelu (detekcja/edycja).
  const fitSignatureRef = useRef<string>("");
  useEffect(() => {
    const map = mapRef.current;
    const layer = eventsLayerRef.current;
    if (!map || !layer || !data) return;
    layer.clearLayers();
    const bounds = L.latLngBounds([]);

    if (data.route_geojson) {
      try {
        const route = L.geoJSON(data.route_geojson as GeoJSON.GeoJsonObject, {
          style: { color: "#dc2626", weight: 3, opacity: 0.9 },
        });
        route.addTo(layer);
        const b = route.getBounds();
        if (b.isValid()) bounds.extend(b);
      } catch { /* zła geometria — mapa zostaje bez trasy */ }
    }
    const dg = data.analysis.display_geom as
      | { features?: { geometry: GeoJSON.GeoJsonObject; properties?: Record<string, unknown> }[] }
      | null;
    for (const f of dg?.features ?? []) {
      try {
        if (f.properties?.info === "oszp_segment") {
          L.geoJSON(f.geometry, {
            style: { color: "#1d4ed8", weight: 6, opacity: 0.5, dashArray: "8 6" },
          }).bindTooltip(`Trasa na terenie zalewowym — ~${f.properties?.length_m ?? "?"} m`)
            .addTo(layer);
        } else if (f.properties?.info === "dzialka_wp") {
          // obrys działki wodnej — widać, KTÓREJ działki dotyczy zbliżenie
          L.geoJSON(f.geometry, {
            style: { color: "#0e7490", weight: 2, fillColor: "#0e7490", fillOpacity: 0.15 },
          }).bindTooltip(String(f.properties?.label ?? "działka Wód Polskich"))
            .addTo(layer);
        } else if (f.properties?.info === "oszp_urzadzenie") {
          // urządzenie projektu stojące w strefie zalewowej
          L.geoJSON(f.geometry, {
            pointToLayer: (_f, latlng) => L.circleMarker(latlng, {
              radius: 5, color: "#0284c7", weight: 2, fillColor: "#ffffff", fillOpacity: 1,
            }),
          }).bindTooltip(`Urządzenie na terenie zalewowym: ${String(f.properties?.label ?? "")}`)
            .addTo(layer);
        } else if (f.properties?.info === "zblizenie_miarka") {
          // miarka: OD CZEGO i Z JAKIEJ ODLEGŁOŚCI wykryto zbliżenie
          const miarka = L.geoJSON(f.geometry, {
            style: { color: "#d97706", weight: 2, dashArray: "4 4", opacity: 0.9 },
          }).addTo(layer);
          const label = String(f.properties?.label ?? "");
          if (label) {
            miarka.bindTooltip(
              `Zbliżenie: ${label}${f.properties?.feature_name ? ` — ${f.properties.feature_name}` : ""}`,
              { permanent: true, direction: "center", className: "wody-miarka-label" },
            );
          }
        }
      } catch { /* pomiń zepsutą geometrię */ }
    }
    data.crossings.forEach((c, i) => {
      if (c.lat == null || c.lng == null) return;
      const color = KIND_COLORS[c.kind] ?? "#6b7280";
      const marker = L.circleMarker([c.lat, c.lng], {
        radius: 8,
        color: "#ffffff",
        weight: 2,
        fillColor: c.is_excluded ? "#9ca3af" : color,
        fillOpacity: c.is_excluded ? 0.4 : 0.9,
      }).addTo(layer);
      marker.bindTooltip(
        `${i + 1}. ${c.kind_display}${c.feature_name ? ` — ${c.feature_name}` : ""}`
        + `${inFloodZone(c) && c.kind !== "teren_zalewowy" ? " · teren zalewowy" : ""}${c.is_excluded ? " · wykluczone" : ""}`,
      );
      // numer w środku punktu — spójny z kolumną # w tabeli
      L.marker([c.lat, c.lng], {
        interactive: false,
        icon: L.divIcon({
          className: "",
          html: `<div style="font:700 9px monospace;color:#fff;text-align:center;line-height:16px;width:16px;">${i + 1}</div>`,
          iconSize: [16, 16],
          iconAnchor: [8, 8],
        }),
      }).addTo(layer);
      bounds.extend([c.lat, c.lng]);
    });
    // Dopasuj widok tylko przy zmianie ZBIORU obiektów — toggle notatki/wykluczenia
    // nie może wyrywać użytkownikowi zoomu.
    const signature = data.crossings.map((c) => c.id).sort().join(",") + `|route:${!!data.route_geojson}`;
    if (bounds.isValid() && signature !== fitSignatureRef.current) {
      fitSignatureRef.current = signature;
      map.fitBounds(bounds.pad(0.25), { maxZoom: 17 });
    }
    // mapReady w zależnościach: bez tego warstwa narysowana PRZED powstaniem mapy
    // przepadała i zostawał widok całej Polski (wyścig init ↔ dane).
  }, [data, mapReady]);

  // Zwykła lista kandydatów (bez symulacji) — do formularza, gdy nie symulowano cen.
  const contractorsQ = useQuery({
    queryKey: ["pp", "wody-contractors", connectionId],
    queryFn: () => getWodyContractors(connectionId),
    enabled: showForm && !simulateM.data,
    staleTime: 5 * 60_000,
  });

  if (isLoading || !data) {
    return <div className="flex justify-center py-16"><Spinner /></div>;
  }

  const canManage = data.can_manage;
  const contractorMode = data.mode === "contractor";
  const running = !!liveTaskId && !taskDone;
  const freeCount = data.crossings.filter((c) => !c.is_frozen && !c.is_excluded).length;
  const activeCount = data.crossings.filter((c) => !c.is_excluded).length;
  const computed = !!data.analysis.computed_at;
  // Przełącznik trybu ma sens dopiero, gdy jest CO zlecać — przy zerze zdarzeń
  // (i bez zamówień) pokazujemy zamiast niego wniosek z detekcji.
  const showModeCard = activeCount > 0 || data.orders.length > 0;

  const ratingStars = (rating: number | null, count: number) =>
    rating != null ? (
      <span className="inline-flex items-center gap-0.5 text-amber-500" title={`${count} ocen`}>
        <Star size={12} fill="currentColor" /> {rating.toFixed(1)}
      </span>
    ) : (
      <span className="text-[10px] text-[var(--color-text-muted)]">brak ocen</span>
    );

  const simulated = simulateM.data?.contractors ?? [];
  const contractorOptions = (simulated.length
    ? simulated.map((c) => ({
        value: c.id,
        label: `${c.name} · ${c.amount_net} zł netto (symulacja)`,
        hint: c.note || undefined,
      }))
    : (contractorsQ.data ?? []).map((c) => ({
        value: c.id,
        label: c.name,
        hint: c.matched_items.length ? `Pozycje wodnoprawne: ${c.matched_items.slice(0, 3).join("; ")}` : undefined,
      })));

  return (
    <div className="space-y-4">
      {/* ── tryb realizacji (tylko gdy jest co zlecać) ── */}
      {!showModeCard && (
        <SectionCard title="Wody Polskie — tryb realizacji">
          {computed ? (
            <p className="text-sm text-[var(--color-text)]">
              Detekcja nie wykryła żadnych przekroczeń, zbliżeń, działek Wód Polskich ani urządzeń
              na terenie zalewowym — temat najpewniej <b>nie dotyczy</b> tego projektu, więc wybór
              wykonawcy jest zbędny.
              Na grafie postępów (zakładka Kamienie / Doradca) czeka niebieska sugestia
              ustawienia kamienia Wód Polskich na „Nie dotyczy”.
            </p>
          ) : (
            <p className="text-sm text-[var(--color-text-muted)]">
              Najpierw uruchom detekcję („Przelicz”) — wybór „robimy my / robi podwykonawca”
              pojawi się, gdy na trasie będzie cokolwiek do uzgodnienia z Wodami Polskimi
              (albo po dodaniu pozycji ręcznej).
            </p>
          )}
        </SectionCard>
      )}
      {showModeCard && (
      <SectionCard title="Wody Polskie — tryb realizacji">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          {([
            ["internal", "Robimy my (po staremu)"],
            ["contractor", "Robi podwykonawca (zamówienie zewnętrzne)"],
          ] as [WodyMode, string][]).map(([value, label]) => (
            <label key={value} className={`flex items-center gap-1.5 ${canManage ? "cursor-pointer" : "opacity-70"}`}>
              <input
                type="radio"
                className="accent-[var(--color-primary)]"
                checked={data.mode === value}
                disabled={!canManage || modeM.isPending}
                onChange={() => modeM.mutate(value)}
              />
              {label}
            </label>
          ))}
          {modeM.isError && <span className="text-xs text-red-600">{apiErr(modeM.error)}</span>}
        </div>
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          W trybie „robimy my” wszystko działa jak dotąd — detekcja poniżej jest czysto informacyjna.
          W trybie podwykonawcy wykryte przekroczenia i zbliżenia stają się zakresem zamówienia zewnętrznego.
        </p>
        {data.wody_milestones.length === 0 && (
          <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            Ten projekt <b>nie ma jeszcze kamieni Wód Polskich</b> na schemacie postępów
            (tablica sprzed wprowadzenia modułu).{" "}
            {canManage ? (
              <button
                className="font-semibold underline"
                onClick={() => modeM.mutate(data.mode)}
                disabled={modeM.isPending}
              >
                {contractorMode
                  ? "Utwórz kamień „Wody Polskie”"
                  : "Utwórz kamienie „WP warunki” + „Pozwolenie WP”"}
              </button>
            ) : (
              <span>Kamienie doda kierownik przedsięwzięcia lub dyrektor (przełączając/zatwierdzając tryb).</span>
            )}
          </div>
        )}
        {data.wody_milestones.length > 0 && (
          <div className="mt-3 rounded border border-[var(--color-border)] p-2">
            <p className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
              Kamienie Wód Polskich na tym projekcie ({data.wody_milestones.length})
            </p>
            <div className="mt-1 flex flex-wrap gap-2">
              {data.wody_milestones.map((m) => (
                <span key={m.key} className="inline-flex items-center gap-1.5 rounded bg-[var(--color-bg-muted)] px-2 py-1 text-xs">
                  {m.label}
                  <span className={`rounded px-1 font-mono text-[10px] ${
                    m.state === "done" ? "bg-emerald-100 text-emerald-700"
                    : m.state === "na" ? "bg-gray-200 text-gray-600"
                    : m.state === "submitted" || m.state === "in_progress" ? "bg-amber-100 text-amber-700"
                    : "bg-gray-100 text-gray-500"
                  }`}>
                    {m.state_display}
                  </span>
                </span>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-[var(--color-text-muted)]">
              {data.wody_milestones.length >= 2 ? (
                <>Przełączenie na tryb podwykonawcy <b>scali te {data.wody_milestones.length} kamienie w jeden</b> „Wody
                Polskie” (stan bardziej zaawansowanego kroku wygrywa; historia, notatki i pliki przechodzą na scalony).</>
              ) : (
                <>Przełączenie na tryb „robimy my” <b>rozdzieli ten kamień na dwa</b> („WP warunki” + „Pozwolenie WP”),
                z odtworzeniem stanów; historia i pliki przechodzą na krok pozwolenia.</>
              )}
            </p>
          </div>
        )}
      </SectionCard>
      )}

      {/* ── detekcja ── */}
      <SectionCard
        title="Detekcja przekroczeń i zbliżeń"
        action={
          canManage && (
            <Button size="sm" onClick={() => recomputeM.mutate()} disabled={running || recomputeM.isPending}>
              {running ? "Przeliczanie…" : "Przelicz"}
            </Button>
          )
        }
      >
        {data.dead_scope && (
          <div className="mb-3 flex flex-wrap items-center gap-2 border border-[var(--color-primary)] bg-[color:rgba(220,31,31,0.06)] p-2 text-xs text-[var(--color-primary)]">
            <AlertTriangle size={13} className="shrink-0" />
            <span className="flex-1">
              {data.dead_scope.kind === "order" ? "Zlecenie" : "Postępowanie"}{" "}
              <b>{data.dead_scope.label}</b>{" "}
              {data.dead_scope.kind === "order"
                ? "zostało odrzucone przez podwykonawcę"
                : "zostało anulowane"}
              , a wykaz został przy nim zamrożony — dlatego nie da się zlecić przekroczeń
              ponownie. {data.dead_scope.hint}
            </span>
          </div>
        )}
        {data.analysis.is_stale && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            <AlertTriangle size={13} className="shrink-0" />
            <span className="flex-1">
              Detekcja liczona na wersji <b>{data.analysis.analysis_route_label || "—"}</b>,
              bieżąca trasa to <b>{data.analysis.current_route_label || "—"}</b> — przelicz.
            </span>
            {canManage && (
              <Button
                size="sm"
                onClick={() => recomputeM.mutate()}
                disabled={running || recomputeM.isPending}
              >
                {running ? "Przeliczanie…" : "Przelicz"}
              </Button>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--color-text-muted)]">
          <span>
            Ostatnie przeliczenie:{" "}
            <b className="text-[var(--color-text)]">
              {data.analysis.computed_at ? fmtDate(data.analysis.computed_at) : "nigdy"}
            </b>
          </span>
          {Object.entries(data.analysis.sources ?? {}).map(([src, st]) => (
            <span key={src} className={`rounded px-1.5 py-0.5 font-mono ${st === "ok" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-red-100 text-red-700"}`}>
              {src}: {st}
            </span>
          ))}
        </div>
        {recomputeM.isError && (
          <p className="mt-2 text-xs text-red-600">{apiErr(recomputeM.error)}</p>
        )}
        {running && (
          <div className="mt-2 flex items-center gap-2 text-xs">
            <Spinner size="sm" /> Detekcja w toku…
          </div>
        )}
        {!running && lastTask?.status === "cancelled" && (
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            Ostatnia detekcja została przerwana — wynik poniżej pochodzi z wcześniejszego przebiegu.
          </p>
        )}
        {!running && lastTask?.status === "failure" && (
          <div className="mt-2 flex items-start gap-1.5 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700 dark:bg-red-950/40">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            <span>Ostatnia detekcja nie powiodła się: {lastTask.error || "błąd bez opisu."}</span>
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {data.kinds.map((k) => (
            <span key={k.value} className="rounded bg-[var(--color-bg-muted)] px-2 py-1 font-mono text-[11px]">
              {k.label}: <b>{data.counts[k.value] ?? 0}</b>
            </span>
          ))}
        </div>
        <div className="mt-2 text-[11px] text-[var(--color-text-muted)]">
          <p className="font-semibold text-[var(--color-text)]">Kryteria detekcji:</p>
          <ul className="ml-4 list-disc space-y-0.5">
            <li><b>Przejście</b> — oś trasy przecina ciek z MPHP (Hydroportal Wód Polskich); rodzaj (ziemne / napowietrzne / przebudowa / demontaż) z otypowanych linii trasy.</li>
            <li><b>Zbliżenie do wału</b> — trasa &lt; <b>50 m</b> od obiektu wałowego z BDOT10k (art. 176 ust. 1 pkt 4 Prawa wodnego); zmierzona odległość w kolumnie i na miarce na mapie. Warstwa zawiera też groble/nasypy — „rodzaj nieustalony” zweryfikuj i w razie czego wyklucz.</li>
            <li><b>Działka WP</b> — trasa wchodzi na działkę Wód Polskich (właściciel/władający albo użytek Wp/Ws) bez przecięcia osi cieku; zajęcie gruntu pod wodami rozliczane jak <b>przejście</b> (rodzaj z linii), nie „zbliżenie”.</li>
            <li><b>Teren zalewowy</b> — odcinek trasy albo urządzenie projektu (stacja, złącze, słup; bez istniejących) na terenie zalewowym (mapy zagrożenia powodziowego Wód Polskich). Projektowanie na terenie zalewowym każdorazowo wymaga <b>operatu wodnoprawnego</b> — osobna pozycja „Urządzenie na terenie zalewowym”, chyba że to miejsce rozlicza już przejście albo zbliżenie na terenie zalewowym. Pozostałe zdarzenia dostają flagę w kolumnie „Teren zalewowy”.</li>
          </ul>
          <p className="mt-1">Zbliżenia do wałów w promieniu 150 m od punktu przejścia są pomijane (obwałowania
          przekraczanego cieku to część przejścia), zbliżenia bliżej niż 150 m <b>od siebie</b> liczą się jako
          jedno, nasypy nazwane wprost w BDOT10k są odsiewane, a wiersze „do przejrzenia” wymagają decyzji
          człowieka.</p>
        </div>
      </SectionCard>

      {/* ── wykaz ── */}
      <SectionCard
        title={`Wykaz przekroczeń i zbliżeń (${activeCount}${data.crossings.length > activeCount ? ` + ${data.crossings.length - activeCount} wykl.` : ""})`}
        action={
          <div className="flex items-center gap-2">
            {/* ile pozycji wykazu nie trafiło jeszcze do żadnego zamówienia — ta sama
                liczba, którą tablica kamieni pokazuje na karcie „Wody Polskie" */}
            {!!data.pending_count && (
              <span
                className="border border-[#d97706] px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wide text-[#d97706]"
                title="Zdarzenia wykazu, które nie trafiły jeszcze do zamówienia podwykonawczego"
              >
                {data.pending_count} do zlecenia
              </span>
            )}
            {canManage && (
              <Button size="sm" variant="secondary" onClick={() => setAddOpen((v) => !v)}>
                <Plus size={13} /> Dodaj ręcznie
              </Button>
            )}
          </div>
        }
      >
        {addOpen && (
          <div className="mb-3 flex flex-wrap items-end gap-2 rounded border border-[var(--color-border)] p-2 text-xs">
            {(["lat", "lng"] as const).map((f) => (
              <label key={f} className="flex flex-col gap-0.5">
                <span className="font-mono uppercase text-[10px] text-[var(--color-text-muted)]">{f === "lat" ? "Szerokość (lat)" : "Długość (lng)"}</span>
                <input
                  className="input-base w-28"
                  value={addDraft[f]}
                  onChange={(e) => setAddDraft((d) => ({ ...d, [f]: e.target.value }))}
                  placeholder={f === "lat" ? "52.2297" : "21.0122"}
                />
              </label>
            ))}
            <label className="flex flex-col gap-0.5">
              <span className="font-mono uppercase text-[10px] text-[var(--color-text-muted)]">Rodzaj</span>
              <select
                className="input-base"
                value={addDraft.kind}
                onChange={(e) => setAddDraft((d) => ({ ...d, kind: e.target.value as HydroCrossingKind }))}
              >
                {data.kinds.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="font-mono uppercase text-[10px] text-[var(--color-text-muted)]">Ciek / obiekt / urządzenie</span>
              <input className="input-base w-44" value={addDraft.feature_name} onChange={(e) => setAddDraft((d) => ({ ...d, feature_name: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="font-mono uppercase text-[10px] text-[var(--color-text-muted)]">Notatka</span>
              <input className="input-base w-44" value={addDraft.note} onChange={(e) => setAddDraft((d) => ({ ...d, note: e.target.value }))} />
            </label>
            <Button
              size="sm"
              variant={pickOnMap ? "primary" : "secondary"}
              onClick={() => setPickOnMap((v) => !v)}
              title="Kliknij na mapie, żeby wskazać położenie zdarzenia"
            >
              <MapPin size={13} /> {pickOnMap ? "Zakończ wskazywanie" : "Wskaż na mapie"}
            </Button>
            <Button
              size="sm"
              disabled={addM.isPending || !addDraft.lat || !addDraft.lng}
              onClick={() => addM.mutate({
                connection: connectionId,
                lat: parseFloat(addDraft.lat),
                lng: parseFloat(addDraft.lng),
                kind: addDraft.kind,
                feature_name: addDraft.feature_name,
                note: addDraft.note,
              })}
            >
              Dodaj
            </Button>
            {addM.isError && <span className="text-red-600">{apiErr(addM.error)}</span>}
            {picking && (
              <span className="basis-full text-[11px] text-[var(--color-text-muted)]">
                Kliknij na mapie poniżej — współrzędne wypełnią się same.
              </span>
            )}
          </div>
        )}

        {hasMapContent && (
          <div className="mb-3">
            <div ref={mapDivRef} className="h-80 w-full rounded border border-[var(--color-border)]" />
            <div className="mt-1 flex flex-wrap items-center gap-3 text-[10px] text-[var(--color-text-muted)]">
              <span className="inline-flex items-center gap-1"><span className="inline-block h-0.5 w-5 bg-[#dc2626]" /> trasa</span>
              {data.kinds.map((k) => (
                <span key={k.value} className="inline-flex items-center gap-1">
                  <span className="inline-block h-2.5 w-2.5 rounded-full border border-white shadow" style={{ background: KIND_COLORS[k.value] ?? "#6b7280" }} />
                  {k.label}
                </span>
              ))}
              <span className="inline-flex items-center gap-1">
                <span className="inline-block h-0.5 w-5 bg-[#1d4ed8]" style={{ opacity: 0.6 }} /> trasa na terenie zalewowym
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full border-2 border-[#0284c7] bg-white" /> urządzenie na terenie zalewowym
              </span>
              <label className="inline-flex cursor-pointer items-center gap-1">
                <input
                  type="checkbox"
                  className="accent-[var(--color-primary)]"
                  checked={showZones}
                  onChange={(e) => setShowZones(e.target.checked)}
                />
                <span className="inline-block h-2.5 w-4" style={{ background: "rgba(163,190,255,.8)" }} />
                tereny zalewowe (Wody Polskie)
              </label>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block h-0.5 w-5 border-t-2 border-dashed border-[#d97706]" /> miarka zbliżenia (odległość od trasy)
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block h-2.5 w-2.5 border-2 border-[#0e7490]" style={{ background: "rgba(14,116,144,.15)" }} /> działka Wód Polskich
              </span>
            </div>
          </div>
        )}

        {data.crossings.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">
            Brak zdarzeń — uruchom detekcję („Przelicz”) albo dodaj pozycję ręcznie.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left font-mono uppercase tracking-wider text-[10px] text-[var(--color-text-muted)]">
                  <th className="py-1.5 pr-2">#</th>
                  <th className="py-1.5 pr-2">Rodzaj (rozliczeniowy)</th>
                  <th className="py-1.5 pr-2">Zdarzenie</th>
                  <th className="py-1.5 pr-2" title="Zmierzona odległość trasy od wykrytego obiektu (dotyczy zbliżeń do wałów — kryterium: < 50 m).">Odległość</th>
                  <th className="py-1.5 pr-2">Ciek / obiekt</th>
                  <th className="py-1.5 pr-2">Działka</th>
                  <th className="py-1.5 pr-2" title="Teren zalewowy z map zagrożenia powodziowego Wód Polskich. Roboty na terenie zalewowym wymagają dodatkowych zgód Wód Polskich.">Teren zalewowy</th>
                  <th className="py-1.5 pr-2">Organ WP</th>
                  <th className="py-1.5 pr-2">Notatka</th>
                  <th className="py-1.5 pr-2">Wykl.</th>
                  <th className="py-1.5" />
                </tr>
              </thead>
              <tbody>
                {data.crossings.map((c: HydroCrossingRow, i: number) => (
                  <tr key={c.id} className={`border-b border-[var(--color-border)] ${c.is_excluded ? "opacity-50" : ""}`}>
                    <td className="py-1.5 pr-2 font-mono">{i + 1}</td>
                    <td className="py-1.5 pr-2">
                      {c.is_frozen ? (
                        <span className="inline-flex items-center gap-1" title={`Zamrożone — ${c.order_number ?? "postępowanie"}`}>
                          <Lock size={11} /> {c.kind_display}
                        </span>
                      ) : (
                        <select
                          className="input-base py-0.5 text-xs"
                          value={c.kind}
                          disabled={!canManage || patchM.isPending}
                          onChange={(e) => patchM.mutate({ id: c.id, patch: { kind: e.target.value as HydroCrossingKind } })}
                        >
                          {data.kinds.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                        </select>
                      )}
                      {/* „Raczej nie" wypiera „do przejrzenia": to mocniejsze zdanie
                          o tym samym wierszu, a dwie plakietki obok siebie tylko myliły. */}
                      {c.confidence === "unlikely" ? (
                        <span
                          className="ml-1 inline-flex items-center gap-0.5 text-red-600"
                          title={
                            "Wał chroni przed wodą, a tu jej nie ma: w promieniu 200 m nie " +
                            "znaleziono cieku, a punkt leży poza terenem zalewowym. " +
                            "To raczej skarpa, nasyp albo grobla — " +
                            "sprawdź przed zlecaniem uzgodnienia."
                          }
                        >
                          <AlertTriangle size={11} /> raczej nie wał
                        </span>
                      ) : c.needs_review ? (
                        <span className="ml-1 inline-flex items-center gap-0.5 text-amber-600" title="Klasyfikacja niepewna — potwierdź rodzaj.">
                          <AlertTriangle size={11} /> do przejrzenia
                        </span>
                      ) : null}
                    </td>
                    <td className="py-1.5 pr-2">
                      <span className="cursor-help underline decoration-dotted" title={EVENT_CRITERIA[c.event_type] ?? ""}>
                        {EVENT_BADGE[c.event_type] ?? c.event_type_display}
                      </span>
                    </td>
                    <td className="py-1.5 pr-2 font-mono">
                      {c.distance_m != null ? `${c.distance_m} m` : "—"}
                    </td>
                    <td className="py-1.5 pr-2">{c.feature_name || "—"}</td>
                    <td className="py-1.5 pr-2 font-mono">{c.parcel_number ?? "—"}</td>
                    <td className="py-1.5 pr-2">
                      {inFloodZone(c)
                        ? <span className="rounded bg-red-100 px-1 font-mono text-[10px] text-red-700" title="Punkt leży na terenie zalewowym.">TAK</span>
                        : <span className="text-[var(--color-text-muted)]">nie</span>}
                    </td>
                    <td className="py-1.5 pr-2">{c.wp_office?.nadzor || c.wp_office?.zarzad || c.wp_office?.rzgw || "—"}</td>
                    <td className="py-1.5 pr-2">
                      <input
                        className="input-base w-36 py-0.5 text-xs"
                        defaultValue={c.note}
                        disabled={!canManage || c.is_frozen}
                        onBlur={(e) => {
                          if (e.target.value !== c.note) patchM.mutate({ id: c.id, patch: { note: e.target.value } });
                        }}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <input
                        type="checkbox"
                        className="accent-[var(--color-primary)]"
                        checked={c.is_excluded}
                        disabled={!canManage || c.is_frozen || patchM.isPending}
                        onChange={(e) => patchM.mutate({ id: c.id, patch: { is_excluded: e.target.checked } })}
                        title="Wyklucz ze zlecenia"
                      />
                    </td>
                    <td className="py-1.5 text-right">
                      {c.is_manual && !c.is_frozen && canManage && (
                        <button
                          className="text-[var(--color-text-muted)] hover:text-red-600"
                          onClick={() => delM.mutate(c.id)}
                          title="Usuń pozycję ręczną"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {patchM.isError && <p className="mt-1 text-xs text-red-600">{apiErr(patchM.error)}</p>}
        {delM.isError && <p className="mt-1 text-xs text-red-600">{apiErr(delM.error)}</p>}
      </SectionCard>

      {/* ── niebieskie propozycje stanów kamieni wodnych (fakty z zamówień/detekcji) ── */}
      {(data.milestone_proposals ?? []).length > 0 && (
        <div className="space-y-1.5">
          {(data.milestone_proposals ?? []).map((p) => (
            <div key={`${p.node}-${p.proposed_state}`} className="flex flex-wrap items-center gap-2 rounded border border-blue-300 bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
              <span>
                Kamień <b>{p.label}</b>: propozycja zmiany stanu na{" "}
                <b>{milestoneMeta(p.proposed_state as MilestoneState).label}</b>
                {" — "}{p.reason}
              </span>
              {canManage && (
                <Button
                  size="sm"
                  disabled={applyProposalM.isPending}
                  onClick={() => applyProposalM.mutate({ node: p.node, state: p.proposed_state, reason: p.reason })}
                >
                  Zastosuj
                </Button>
              )}
            </div>
          ))}
          {applyProposalM.isError && (
            <p className="text-xs text-red-600">{apiErr(applyProposalM.error)}</p>
          )}
        </div>
      )}

      {/* ── zlecenie podwykonawcy (tryb contractor + jest co zlecać/pokazać) ── */}
      {contractorMode && showModeCard && (
        <SectionCard
          title="Zlecenie podwykonawcy"
          action={
            canManage && !showForm && (
              <Button size="sm" onClick={() => setShowForm(true)} disabled={freeCount === 0}>
                Zleć podwykonawcy
              </Button>
            )
          }
        >
          {freeCount === 0 && !data.orders.length && (
            <p className="text-sm text-[var(--color-text-muted)]">
              Brak niezleconych przekroczeń/zbliżeń — najpierw uruchom detekcję.
            </p>
          )}

          {canManage && (
            <div className="mb-3">
              <Button size="sm" variant="secondary" onClick={() => simulateM.mutate()} disabled={simulateM.isPending || freeCount === 0}>
                {simulateM.isPending ? "Symulowanie cen…" : "Symuluj ceny kandydatów (AI)"}
              </Button>
              {simulateM.isError && <span className="ml-2 text-xs text-red-600">{apiErr(simulateM.error)}</span>}
            </div>
          )}
          {simulated.length > 0 && (
            <div className="mb-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[var(--color-border)] text-left font-mono uppercase tracking-wider text-[10px] text-[var(--color-text-muted)]">
                    <th className="py-1.5 pr-2">Podwykonawca</th>
                    <th className="py-1.5 pr-2">Cena symulowana (netto)</th>
                    <th className="py-1.5 pr-2">Rating</th>
                    <th className="py-1.5" />
                  </tr>
                </thead>
                <tbody>
                  {simulated.map((c) => (
                    <>
                      <tr key={c.id} className="border-b border-[var(--color-border)]">
                        <td className="py-1.5 pr-2 font-semibold">{c.name}</td>
                        <td className="py-1.5 pr-2 font-mono font-semibold">{c.amount_net} zł</td>
                        <td className="py-1.5 pr-2">{ratingStars(c.rating, c.rating_count)}</td>
                        <td className="py-1.5 text-right">
                          {canManage && (
                            <Button size="sm" variant="secondary" onClick={() => { setPickedContractor(c); setShowForm(true); }}>
                              Zleć tej firmie
                            </Button>
                          )}
                        </td>
                      </tr>
                      {c.items.map((it) => (
                        <tr key={`${c.id}-${it.line_item_type}`} className="border-b border-dashed border-[var(--color-border)] text-[var(--color-text-muted)]">
                          <td className="py-1 pl-4 pr-2" title={it.reason || undefined}>
                            {it.name || "(pozycja umowy ramowej)"}
                          </td>
                          <td className="py-1 pr-2 font-mono" colSpan={3}>
                            {it.quantity} {it.unit || "szt."} × {it.rate} zł = <b className="text-[var(--color-text)]">{it.total} zł</b>
                          </td>
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
              </table>
              <p className="mt-1 text-[10px] text-[var(--color-text-muted)]">
                Ceny symulowane pozycjami z umów ramowych kandydatów (AI) — ostateczne kwoty w zamówieniu
                i tak liczą się ze stawek umowy ramowej wybranej firmy.
              </p>
              {(simulateM.data?.errors?.length ?? 0) > 0 && (
                <div className="mt-1 rounded border border-amber-300 bg-amber-50 p-1.5 text-[10px] text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                  Nie wyceniono: {simulateM.data!.errors.join("; ")}
                </div>
              )}
              {(simulateM.data?.skipped ?? 0) > 0 && (
                <p className="mt-1 text-[10px] text-[var(--color-text-muted)]">
                  Pominięto {simulateM.data!.skipped} dalszych kandydatów (limit symulacji).
                </p>
              )}
            </div>
          )}

          {showForm && (
            <DesignOrderForm
              pending={orderM.isPending}
              onSubmit={(fd) => orderM.mutate(fd)}
              onCancel={() => { setShowForm(false); setPickedContractor(null); }}
              aiSuggest={(contractorId) => suggestWodyItems(connectionId, contractorId)}
              defaultContractorId={pickedContractor?.id}
              contractorsOverride={{
                options: pickedContractor
                  ? [{ value: pickedContractor.id, label: `${pickedContractor.name} · ${pickedContractor.amount_net} zł netto (symulacja)` }]
                  : contractorOptions,
                loading: !simulated.length && contractorsQ.isLoading,
                emptyText: "Żaden aktywny podwykonawca nie ma pozycji wodnoprawnych w umowie ramowej.",
              }}
              checkpointPath="wody"
              connectionId={connectionId}
              showProjectFileShare
            />
          )}
          {orderM.isError && <p className="mt-2 text-xs text-red-600">{apiErr(orderM.error)}</p>}

          {data.orders.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {data.orders.map((o) => (
                <div key={o.id} className="flex flex-wrap items-center gap-2 rounded border border-[var(--color-border)] px-2 py-1.5 text-xs">
                  {canOpenOrder ? (
                    <Link to={`/orders/${o.order}`} className="inline-flex items-center gap-1 font-mono text-[var(--color-primary)] hover:underline">
                      {o.order_number} <ExternalLink size={11} />
                    </Link>
                  ) : (
                    <span className="font-mono">{o.order_number}</span>
                  )}
                  <span>{o.contractor_name}</span>
                  <span className="rounded bg-[var(--color-bg-muted)] px-1.5 py-0.5 font-mono text-[10px]">{o.order_status_display}</span>
                  <span className="text-[var(--color-text-muted)]">{o.crossings_count} pozycji wykazu</span>
                  {o.deadline && <span className="text-[var(--color-text-muted)]">termin {fmtDate(o.deadline)}</span>}
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}
    </div>
  );
}
